// 변경 스트림(SSE). 프레임은 어떤 자원이 어떤 면에서 낡았는지만 말한다 —
// 값은 화면이 평소의 목록 API 로 다시 읽는다. 재접속은 브라우저의
// EventSource 가 `retry:` 힌트와 Last-Event-ID 로 하고, 브라우저가 포기한
// 연결(닫힘 상태)만 여기서 지수 백오프로 다시 연다. 다시 열린 뒤의 첫
// open 은 놓친 사이가 있을 수 있으므로 resync 로 취급한다.

export type Change = { kind: string; id: string; what: string }

export type StreamHandlers = {
  onChange: (changes: Change[]) => void
  /** 놓친 구간이 있을 수 있다 — 전부 다시 읽어야 한다. */
  onResync: () => void
  onConnected: (connected: boolean) => void
}

export const STREAM_PATH = '/api/v1/events/stream'

const RETRY_MIN_MS = 1000
const RETRY_MAX_MS = 30000

export function openStream(handlers: StreamHandlers): () => void {
  if (typeof EventSource === 'undefined') return () => {}

  let alive = true
  let source: EventSource | null = null
  let retry: number | undefined
  let delay = RETRY_MIN_MS
  let everOpened = false

  const connect = () => {
    if (!alive) return
    const es = new EventSource(STREAM_PATH)
    source = es
    es.addEventListener('open', () => {
      delay = RETRY_MIN_MS
      if (everOpened) handlers.onResync()
      everOpened = true
      handlers.onConnected(true)
    })
    es.addEventListener('change', (event) => {
      const changes = parseChanges((event as MessageEvent<string>).data)
      if (changes.length > 0) handlers.onChange(changes)
    })
    es.addEventListener('resync', () => handlers.onResync())
    es.addEventListener('error', () => {
      handlers.onConnected(false)
      // CONNECTING 은 브라우저가 스스로 다시 붙는 중이다. CLOSED 만 우리 몫이다.
      if (es.readyState !== EventSource.CLOSED) return
      es.close()
      if (source === es) source = null
      if (!alive || retry !== undefined) return
      retry = window.setTimeout(() => {
        retry = undefined
        delay = Math.min(delay * 2, RETRY_MAX_MS)
        connect()
      }, delay)
    })
  }

  connect()
  return () => {
    alive = false
    source?.close()
    source = null
    if (retry !== undefined) window.clearTimeout(retry)
  }
}

export function parseChanges(data: string): Change[] {
  let parsed: unknown
  try {
    parsed = JSON.parse(data)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []
  const out: Change[] = []
  for (const item of parsed) {
    if (typeof item !== 'object' || item === null) continue
    const { kind, id, what } = item as Record<string, unknown>
    if (typeof kind === 'string' && typeof id === 'string' && typeof what === 'string') {
      out.push({ kind, id, what })
    }
  }
  return out
}
